import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Shield, Menu, X, Globe, ChevronDown, FileText, Users, Scale, 
  Briefcase, AlertTriangle, Heart, Phone, Mail, MapPin,
  CheckCircle, MessageSquare, Clock, ArrowRight
} from "lucide-react";

// Translation strings for each language
const translations = {
  en: {
    nav: { home: "Home", howItWorks: "How It Works", services: "Services", pricing: "Pricing", faq: "FAQ", contact: "Contact" },
    hero: {
      title: "Protecting Workers, Defending Rights",
      subtitle: "MyShield provides comprehensive support for foreign workers in Malaysia. From salary disputes to legal assistance, we're here to help.",
      getStarted: "Get Started",
      learnMore: "Learn More"
    },
    howItWorks: {
      title: "How It Works",
      subtitle: "Three simple steps to get the help you need",
      steps: [
        { title: "Report Your Issue", description: "Fill out our secure intake form with details about your situation." },
        { title: "Get Assigned Support", description: "A dedicated staff member will be assigned to handle your case." },
        { title: "Track & Resolve", description: "Monitor your case progress and communicate directly through our platform." }
      ]
    },
    services: {
      title: "Our Services",
      subtitle: "Comprehensive support for all worker protection needs",
      items: [
        { title: "Salary Disputes", description: "Assistance with unpaid wages, illegal deductions, and overtime claims." },
        { title: "Welfare Benefits", description: "Guidance on medical coverage, insurance claims, and worker compensation." },
        { title: "Harassment & Abuse", description: "Confidential support and reporting for workplace harassment or physical abuse." },
        { title: "Contract Issues", description: "Help with contract violations, unfair terms, and employment disputes." },
        { title: "Workplace Conditions", description: "Reporting unsafe working environments and accommodation issues." },
        { title: "Legal Support", description: "Access to legal advice and representation for worker rights cases." }
      ]
    },
    pricing: {
      title: "Membership Plans",
      subtitle: "Choose your protection plan",
      perMonth: "/month",
      total: "Total",
      save: "Save",
      fullPrice: "Full price",
      off: "off",
      bestValue: "Best value",
      selectPlan: "Select Plan",
      plans: [
        { months: 1, pricePerMonth: 20, total: 20, savings: 0, discount: null },
        { months: 3, pricePerMonth: 19, total: 57, savings: 3, discount: "5%" },
        { months: 6, pricePerMonth: 18, total: 108, savings: 12, discount: "10%" },
        { months: 12, pricePerMonth: 17, total: 204, savings: 36, discount: "15%", bestValue: true }
      ]
    },
    faq: {
      title: "Frequently Asked Questions",
      subtitle: "Everything you need to know about MyShield",
      items: [
        { q: "Who can use MyShield?", a: "MyShield is designed for all foreign workers in Malaysia, regardless of nationality or employment sector." },
        { q: "How quickly will I get help?", a: "Most cases are assigned within 24 hours. Emergency cases are prioritized and handled immediately." },
        { q: "Is my information confidential?", a: "Yes, all your personal information and case details are kept strictly confidential and secure." },
        { q: "What languages do you support?", a: "We support multiple languages including English, Bahasa Melayu, Tamil, Hindi, Bengali, Nepali, Filipino, Burmese, and Thai." },
        { q: "Can I cancel my membership?", a: "Yes, you can cancel your membership at any time. Your active cases will still be supported until resolution." }
      ]
    },
    contact: {
      title: "Contact Us",
      subtitle: "We're here to help you",
      address: "Level 15, Menara KL\nJalan Sultan Ismail\n50250 Kuala Lumpur",
      phone: "+60 3-1234 5678",
      email: "support@myshield.my",
      hours: "Mon-Fri: 9AM - 6PM\nEmergency: 24/7"
    },
    footer: {
      tagline: "Protecting workers, defending rights.",
      quickLinks: "Quick Links",
      legal: "Legal",
      privacy: "Privacy Policy",
      terms: "Terms of Service",
      copyright: "© 2024 MyShield. All rights reserved."
    },
    cta: {
      title: "Ready to Get Protected?",
      subtitle: "Join thousands of workers who trust MyShield for their protection and support needs.",
      button: "Register Now"
    }
  },
  ms: {
    nav: { home: "Laman Utama", howItWorks: "Cara Ia Berfungsi", services: "Perkhidmatan", pricing: "Harga", faq: "Soalan Lazim", contact: "Hubungi" },
    hero: {
      title: "Melindungi Pekerja, Mempertahankan Hak",
      subtitle: "MyShield menyediakan sokongan menyeluruh untuk pekerja asing di Malaysia. Dari pertikaian gaji hingga bantuan undang-undang, kami di sini untuk membantu.",
      getStarted: "Mula Sekarang",
      learnMore: "Ketahui Lebih"
    },
    howItWorks: {
      title: "Cara Ia Berfungsi",
      subtitle: "Tiga langkah mudah untuk mendapatkan bantuan",
      steps: [
        { title: "Laporkan Isu Anda", description: "Isi borang aduan kami dengan butiran situasi anda." },
        { title: "Dapatkan Sokongan", description: "Kakitangan khusus akan ditugaskan untuk mengendalikan kes anda." },
        { title: "Jejak & Selesaikan", description: "Pantau kemajuan kes dan berkomunikasi terus melalui platform kami." }
      ]
    },
    services: {
      title: "Perkhidmatan Kami",
      subtitle: "Sokongan menyeluruh untuk semua keperluan perlindungan pekerja",
      items: [
        { title: "Pertikaian Gaji", description: "Bantuan dengan gaji tidak dibayar, potongan haram, dan tuntutan kerja lebih masa." },
        { title: "Faedah Kebajikan", description: "Panduan tentang perlindungan perubatan, tuntutan insurans, dan pampasan pekerja." },
        { title: "Gangguan & Penderaan", description: "Sokongan sulit untuk gangguan tempat kerja atau penderaan fizikal." },
        { title: "Isu Kontrak", description: "Bantuan dengan pelanggaran kontrak, terma tidak adil, dan pertikaian pekerjaan." },
        { title: "Keadaan Tempat Kerja", description: "Melaporkan persekitaran kerja tidak selamat dan isu penginapan." },
        { title: "Sokongan Undang-Undang", description: "Akses kepada nasihat undang-undang dan perwakilan untuk kes hak pekerja." }
      ]
    },
    pricing: {
      title: "Pelan Keahlian",
      subtitle: "Pilih pelan perlindungan anda",
      perMonth: "/bulan",
      total: "Jumlah",
      save: "Jimat",
      fullPrice: "Harga penuh",
      off: "diskaun",
      bestValue: "Nilai terbaik",
      selectPlan: "Pilih Pelan",
      plans: [
        { months: 1, pricePerMonth: 20, total: 20, savings: 0, discount: null },
        { months: 3, pricePerMonth: 19, total: 57, savings: 3, discount: "5%" },
        { months: 6, pricePerMonth: 18, total: 108, savings: 12, discount: "10%" },
        { months: 12, pricePerMonth: 17, total: 204, savings: 36, discount: "15%", bestValue: true }
      ]
    },
    faq: {
      title: "Soalan Lazim",
      subtitle: "Semua yang anda perlu tahu tentang MyShield",
      items: [
        { q: "Siapa yang boleh guna MyShield?", a: "MyShield direka untuk semua pekerja asing di Malaysia, tanpa mengira kewarganegaraan atau sektor pekerjaan." },
        { q: "Berapa cepat saya akan dapat bantuan?", a: "Kebanyakan kes ditugaskan dalam masa 24 jam. Kes kecemasan diutamakan dan dikendalikan segera." },
        { q: "Adakah maklumat saya sulit?", a: "Ya, semua maklumat peribadi dan butiran kes anda disimpan dengan sulit dan selamat." },
        { q: "Bahasa apa yang anda sokong?", a: "Kami menyokong pelbagai bahasa termasuk Bahasa Inggeris, Bahasa Melayu, Tamil, Hindi, Bengali, Nepali, Filipino, Burma, dan Thai." },
        { q: "Bolehkah saya batalkan keahlian?", a: "Ya, anda boleh membatalkan keahlian pada bila-bila masa. Kes aktif anda akan terus disokong sehingga selesai." }
      ]
    },
    contact: {
      title: "Hubungi Kami",
      subtitle: "Kami di sini untuk membantu anda",
      address: "Tingkat 15, Menara KL\nJalan Sultan Ismail\n50250 Kuala Lumpur",
      phone: "+60 3-1234 5678",
      email: "support@myshield.my",
      hours: "Isn-Jum: 9PG - 6PTG\nKecemasan: 24/7"
    },
    footer: {
      tagline: "Melindungi pekerja, mempertahankan hak.",
      quickLinks: "Pautan Pantas",
      legal: "Undang-Undang",
      privacy: "Dasar Privasi",
      terms: "Terma Perkhidmatan",
      copyright: "© 2024 MyShield. Hak cipta terpelihara."
    },
    cta: {
      title: "Sedia Untuk Dilindungi?",
      subtitle: "Sertai ribuan pekerja yang mempercayai MyShield untuk perlindungan dan sokongan mereka.",
      button: "Daftar Sekarang"
    }
  },
  ta: {
    nav: { home: "முகப்பு", howItWorks: "எப்படி வேலை செய்கிறது", services: "சேவைகள்", pricing: "விலை", faq: "அடிக்கடி கேள்விகள்", contact: "தொடர்பு" },
    hero: {
      title: "தொழிலாளர்களைப் பாதுகாத்தல், உரிமைகளை பாதுகாத்தல்",
      subtitle: "மலேசியாவில் வெளிநாட்டு தொழிலாளர்களுக்கு MyShield விரிவான ஆதரவை வழங்குகிறது. சம்பள சர்ச்சைகள் முதல் சட்ட உதவி வரை, நாங்கள் உதவ இங்கே இருக்கிறோம்.",
      getStarted: "தொடங்கு",
      learnMore: "மேலும் அறிய"
    },
    howItWorks: {
      title: "எப்படி வேலை செய்கிறது",
      subtitle: "உதவி பெற மூன்று எளிய படிகள்",
      steps: [
        { title: "உங்கள் பிரச்சினையை புகாரளிக்கவும்", description: "உங்கள் நிலைமை பற்றிய விவரங்களுடன் எங்கள் படிவத்தை நிரப்பவும்." },
        { title: "ஆதரவைப் பெறுங்கள்", description: "உங்கள் வழக்கை கையாள ஒரு அர்ப்பணிப்பு ஊழியர் நியமிக்கப்படுவார்." },
        { title: "கண்காணித்து தீர்க்கவும்", description: "உங்கள் வழக்கு முன்னேற்றத்தை கண்காணித்து எங்கள் தளம் வழியாக நேரடியாக தொடர்பு கொள்ளுங்கள்." }
      ]
    },
    services: {
      title: "எங்கள் சேவைகள்",
      subtitle: "அனைத்து தொழிலாளர் பாதுகாப்பு தேவைகளுக்கும் விரிவான ஆதரவு",
      items: [
        { title: "சம்பள சர்ச்சைகள்", description: "செலுத்தப்படாத ஊதியங்கள், சட்டவிரோத கழிவுகள் மற்றும் மேலதிக நேர உரிமைகோரல்களுக்கு உதவி." },
        { title: "நல சலுகைகள்", description: "மருத்துவ காப்பீடு, காப்பீட்டு உரிமைகோரல்கள் மற்றும் தொழிலாளர் இழப்பீடு பற்றிய வழிகாட்டுதல்." },
        { title: "துன்புறுத்தல் மற்றும் துஷ்பிரயோகம்", description: "பணியிட துன்புறுத்தல் அல்லது உடல் ரீதியான துஷ்பிரயோகத்திற்கு ரகசிய ஆதரவு." },
        { title: "ஒப்பந்த சிக்கல்கள்", description: "ஒப்பந்த மீறல்கள், நியாயமற்ற விதிமுறைகள் மற்றும் வேலைவாய்ப்பு சர்ச்சைகளுக்கு உதவி." },
        { title: "பணியிட நிலைமைகள்", description: "பாதுகாப்பற்ற பணி சூழல்கள் மற்றும் தங்குமிட சிக்கல்களை புகாரளிக்கவும்." },
        { title: "சட்ட ஆதரவு", description: "தொழிலாளர் உரிமை வழக்குகளுக்கு சட்ட ஆலோசனை மற்றும் பிரதிநிதித்துவத்திற்கான அணுகல்." }
      ]
    },
    pricing: {
      title: "உறுப்பினர் திட்டங்கள்",
      subtitle: "உங்கள் பாதுகாப்பு திட்டத்தைத் தேர்ந்தெடுக்கவும்",
      perMonth: "/மாதம்",
      total: "மொத்தம்",
      save: "சேமிக்கவும்",
      fullPrice: "முழு விலை",
      off: "தள்ளுபடி",
      bestValue: "சிறந்த மதிப்பு",
      selectPlan: "திட்டத்தைத் தேர்ந்தெடுக்கவும்",
      plans: [
        { months: 1, pricePerMonth: 20, total: 20, savings: 0, discount: null },
        { months: 3, pricePerMonth: 19, total: 57, savings: 3, discount: "5%" },
        { months: 6, pricePerMonth: 18, total: 108, savings: 12, discount: "10%" },
        { months: 12, pricePerMonth: 17, total: 204, savings: 36, discount: "15%", bestValue: true }
      ]
    },
    faq: {
      title: "அடிக்கடி கேட்கப்படும் கேள்விகள்",
      subtitle: "MyShield பற்றி நீங்கள் தெரிந்து கொள்ள வேண்டிய அனைத்தும்",
      items: [
        { q: "யார் MyShield பயன்படுத்தலாம்?", a: "மலேசியாவில் உள்ள அனைத்து வெளிநாட்டு தொழிலாளர்களுக்காக MyShield வடிவமைக்கப்பட்டுள்ளது." },
        { q: "எவ்வளவு விரைவாக எனக்கு உதவி கிடைக்கும்?", a: "பெரும்பாலான வழக்குகள் 24 மணி நேரத்திற்குள் ஒதுக்கப்படும். அவசர வழக்குகள் உடனடியாக கையாளப்படும்." },
        { q: "என் தகவல் ரகசியமா?", a: "ஆம், உங்கள் அனைத்து தனிப்பட்ட தகவல்களும் வழக்கு விவரங்களும் கண்டிப்பாக ரகசியமாக பாதுகாக்கப்படுகின்றன." },
        { q: "எந்த மொழிகளை நீங்கள் ஆதரிக்கிறீர்கள்?", a: "ஆங்கிலம், பஹாசா மலாய், தமிழ், இந்தி, வங்காளம், நேபாளி, பிலிப்பினோ, பர்மிய மற்றும் தாய் உட்பட பல மொழிகளை நாங்கள் ஆதரிக்கிறோம்." },
        { q: "என் உறுப்பினர்களை ரத்து செய்யலாமா?", a: "ஆம், நீங்கள் எந்த நேரத்திலும் உங்கள் உறுப்பினர்களை ரத்து செய்யலாம்." }
      ]
    },
    contact: {
      title: "எங்களை தொடர்பு கொள்ளுங்கள்",
      subtitle: "நாங்கள் உங்களுக்கு உதவ இங்கே இருக்கிறோம்",
      address: "தளம் 15, மெனாரா கேஎல்\nஜாலான் சுல்தான் இஸ்மாயில்\n50250 கோலாலம்பூர்",
      phone: "+60 3-1234 5678",
      email: "support@myshield.my",
      hours: "திங்-வெள்: 9AM - 6PM\nஅவசரம்: 24/7"
    },
    footer: {
      tagline: "தொழிலாளர்களைப் பாதுகாத்தல், உரிமைகளை பாதுகாத்தல்.",
      quickLinks: "விரைவு இணைப்புகள்",
      legal: "சட்டம்",
      privacy: "தனியுரிமைக் கொள்கை",
      terms: "சேவை விதிமுறைகள்",
      copyright: "© 2024 MyShield. அனைத்து உரிமைகளும் பாதுகாக்கப்பட்டவை."
    },
    cta: {
      title: "பாதுகாக்கப்பட தயாரா?",
      subtitle: "தங்கள் பாதுகாப்பு மற்றும் ஆதரவு தேவைகளுக்கு MyShield ஐ நம்பும் ஆயிரக்கணக்கான தொழிலாளர்களுடன் சேருங்கள்.",
      button: "இப்போது பதிவு செய்யுங்கள்"
    }
  },
  hi: {
    nav: { home: "होम", howItWorks: "कैसे काम करता है", services: "सेवाएं", pricing: "मूल्य", faq: "सामान्य प्रश्न", contact: "संपर्क" },
    hero: {
      title: "श्रमिकों की रक्षा, अधिकारों की सुरक्षा",
      subtitle: "MyShield मलेशिया में विदेशी श्रमिकों के लिए व्यापक सहायता प्रदान करता है। वेतन विवाद से लेकर कानूनी सहायता तक, हम मदद के लिए यहां हैं।",
      getStarted: "शुरू करें",
      learnMore: "और जानें"
    },
    howItWorks: {
      title: "कैसे काम करता है",
      subtitle: "मदद पाने के तीन आसान कदम",
      steps: [
        { title: "अपनी समस्या की रिपोर्ट करें", description: "अपनी स्थिति के विवरण के साथ हमारा फॉर्म भरें।" },
        { title: "सहायता प्राप्त करें", description: "आपके मामले को संभालने के लिए एक समर्पित स्टाफ सदस्य नियुक्त किया जाएगा।" },
        { title: "ट्रैक और हल करें", description: "अपने मामले की प्रगति की निगरानी करें और हमारे प्लेटफॉर्म के माध्यम से सीधे संवाद करें।" }
      ]
    },
    services: {
      title: "हमारी सेवाएं",
      subtitle: "सभी श्रमिक सुरक्षा जरूरतों के लिए व्यापक सहायता",
      items: [
        { title: "वेतन विवाद", description: "अवैतनिक मजदूरी, अवैध कटौती और ओवरटाइम दावों में सहायता।" },
        { title: "कल्याण लाभ", description: "चिकित्सा कवरेज, बीमा दावों और श्रमिक मुआवजे पर मार्गदर्शन।" },
        { title: "उत्पीड़न और दुर्व्यवहार", description: "कार्यस्थल उत्पीड़न या शारीरिक दुर्व्यवहार के लिए गोपनीय सहायता।" },
        { title: "अनुबंध मुद्दे", description: "अनुबंध उल्लंघन, अनुचित शर्तों और रोजगार विवादों में मदद।" },
        { title: "कार्यस्थल की स्थितियां", description: "असुरक्षित कार्य वातावरण और आवास मुद्दों की रिपोर्टिंग।" },
        { title: "कानूनी सहायता", description: "श्रमिक अधिकार मामलों के लिए कानूनी सलाह और प्रतिनिधित्व तक पहुंच।" }
      ]
    },
    pricing: {
      title: "सदस्यता योजनाएं",
      subtitle: "अपनी सुरक्षा योजना चुनें",
      perMonth: "/माह",
      total: "कुल",
      save: "बचाएं",
      fullPrice: "पूर्ण मूल्य",
      off: "छूट",
      bestValue: "सर्वोत्तम मूल्य",
      selectPlan: "योजना चुनें",
      plans: [
        { months: 1, pricePerMonth: 20, total: 20, savings: 0, discount: null },
        { months: 3, pricePerMonth: 19, total: 57, savings: 3, discount: "5%" },
        { months: 6, pricePerMonth: 18, total: 108, savings: 12, discount: "10%" },
        { months: 12, pricePerMonth: 17, total: 204, savings: 36, discount: "15%", bestValue: true }
      ]
    },
    faq: {
      title: "अक्सर पूछे जाने वाले प्रश्न",
      subtitle: "MyShield के बारे में वह सब कुछ जो आपको जानना चाहिए",
      items: [
        { q: "MyShield का उपयोग कौन कर सकता है?", a: "MyShield मलेशिया में सभी विदेशी श्रमिकों के लिए डिज़ाइन किया गया है।" },
        { q: "मुझे कितनी जल्दी मदद मिलेगी?", a: "अधिकांश मामले 24 घंटे के भीतर असाइन किए जाते हैं। आपातकालीन मामलों को तुरंत संभाला जाता है।" },
        { q: "क्या मेरी जानकारी गोपनीय है?", a: "हां, आपकी सभी व्यक्तिगत जानकारी और केस विवरण पूरी तरह से गोपनीय रखे जाते हैं।" },
        { q: "आप किन भाषाओं का समर्थन करते हैं?", a: "हम अंग्रेजी, बहासा मलय, तमिल, हिंदी, बंगाली, नेपाली, फिलिपिनो, बर्मी और थाई सहित कई भाषाओं का समर्थन करते हैं।" },
        { q: "क्या मैं अपनी सदस्यता रद्द कर सकता हूं?", a: "हां, आप किसी भी समय अपनी सदस्यता रद्द कर सकते हैं।" }
      ]
    },
    contact: {
      title: "हमसे संपर्क करें",
      subtitle: "हम आपकी मदद के लिए यहां हैं",
      address: "लेवल 15, मेनारा केएल\nजालान सुल्तान इस्माइल\n50250 कुआलालंपुर",
      phone: "+60 3-1234 5678",
      email: "support@myshield.my",
      hours: "सोम-शुक्र: 9AM - 6PM\nआपातकालीन: 24/7"
    },
    footer: {
      tagline: "श्रमिकों की रक्षा, अधिकारों की सुरक्षा।",
      quickLinks: "त्वरित लिंक",
      legal: "कानूनी",
      privacy: "गोपनीयता नीति",
      terms: "सेवा की शर्तें",
      copyright: "© 2024 MyShield. सर्वाधिकार सुरक्षित।"
    },
    cta: {
      title: "सुरक्षित होने के लिए तैयार?",
      subtitle: "हजारों श्रमिकों के साथ जुड़ें जो अपनी सुरक्षा और सहायता जरूरतों के लिए MyShield पर भरोसा करते हैं।",
      button: "अभी रजिस्टर करें"
    }
  },
  bn: {
    nav: { home: "হোম", howItWorks: "কিভাবে কাজ করে", services: "সেবা", pricing: "মূল্য", faq: "প্রশ্নোত্তর", contact: "যোগাযোগ" },
    hero: {
      title: "শ্রমিকদের সুরক্ষা, অধিকারের প্রতিরক্ষা",
      subtitle: "MyShield মালয়েশিয়ায় বিদেশী শ্রমিকদের জন্য ব্যাপক সহায়তা প্রদান করে। বেতন বিরোধ থেকে আইনি সহায়তা পর্যন্ত, আমরা সাহায্য করতে এখানে আছি।",
      getStarted: "শুরু করুন",
      learnMore: "আরও জানুন"
    },
    howItWorks: {
      title: "কিভাবে কাজ করে",
      subtitle: "সাহায্য পেতে তিনটি সহজ পদক্ষেপ",
      steps: [
        { title: "আপনার সমস্যা রিপোর্ট করুন", description: "আপনার পরিস্থিতির বিবরণ দিয়ে আমাদের ফর্ম পূরণ করুন।" },
        { title: "সহায়তা পান", description: "আপনার কেস সামলাতে একজন নিবেদিত কর্মী নিযুক্ত করা হবে।" },
        { title: "ট্র্যাক এবং সমাধান করুন", description: "আপনার কেসের অগ্রগতি পর্যবেক্ষণ করুন এবং আমাদের প্ল্যাটফর্মের মাধ্যমে সরাসরি যোগাযোগ করুন।" }
      ]
    },
    services: {
      title: "আমাদের সেবা",
      subtitle: "সব শ্রমিক সুরক্ষা প্রয়োজনের জন্য ব্যাপক সহায়তা",
      items: [
        { title: "বেতন বিরোধ", description: "অপরিশোধিত মজুরি, অবৈধ কর্তন এবং ওভারটাইম দাবিতে সহায়তা।" },
        { title: "কল্যাণ সুবিধা", description: "চিকিৎসা কভারেজ, বীমা দাবি এবং শ্রমিক ক্ষতিপূরণ সম্পর্কে নির্দেশনা।" },
        { title: "হয়রানি এবং নির্যাতন", description: "কর্মক্ষেত্রে হয়রানি বা শারীরিক নির্যাতনের জন্য গোপনীয় সহায়তা।" },
        { title: "চুক্তি সমস্যা", description: "চুক্তি লঙ্ঘন, অন্যায্য শর্ত এবং কর্মসংস্থান বিরোধে সাহায্য।" },
        { title: "কর্মক্ষেত্রের অবস্থা", description: "অনিরাপদ কাজের পরিবেশ এবং আবাসন সমস্যা রিপোর্ট করা।" },
        { title: "আইনি সহায়তা", description: "শ্রমিক অধিকার মামলার জন্য আইনি পরামর্শ এবং প্রতিনিধিত্বের অ্যাক্সেস।" }
      ]
    },
    pricing: {
      title: "সদস্যতা পরিকল্পনা",
      subtitle: "আপনার সুরক্ষা পরিকল্পনা বেছে নিন",
      perMonth: "/মাস",
      total: "মোট",
      save: "সাশ্রয়",
      fullPrice: "পূর্ণ মূল্য",
      off: "ছাড়",
      bestValue: "সেরা মূল্য",
      selectPlan: "পরিকল্পনা নির্বাচন করুন",
      plans: [
        { months: 1, pricePerMonth: 20, total: 20, savings: 0, discount: null },
        { months: 3, pricePerMonth: 19, total: 57, savings: 3, discount: "5%" },
        { months: 6, pricePerMonth: 18, total: 108, savings: 12, discount: "10%" },
        { months: 12, pricePerMonth: 17, total: 204, savings: 36, discount: "15%", bestValue: true }
      ]
    },
    faq: {
      title: "সাধারণ প্রশ্ন",
      subtitle: "MyShield সম্পর্কে আপনার যা জানা দরকার",
      items: [
        { q: "MyShield কে ব্যবহার করতে পারে?", a: "MyShield মালয়েশিয়ায় সব বিদেশী শ্রমিকদের জন্য ডিজাইন করা হয়েছে।" },
        { q: "কত তাড়াতাড়ি আমি সাহায্য পাব?", a: "বেশিরভাগ কেস 24 ঘণ্টার মধ্যে অ্যাসাইন করা হয়। জরুরি কেস অবিলম্বে পরিচালনা করা হয়।" },
        { q: "আমার তথ্য কি গোপনীয়?", a: "হ্যাঁ, আপনার সব ব্যক্তিগত তথ্য এবং কেসের বিবরণ কঠোরভাবে গোপনীয় রাখা হয়।" },
        { q: "আপনারা কোন ভাষা সমর্থন করেন?", a: "আমরা ইংরেজি, বাহাসা মালয়, তামিল, হিন্দি, বাংলা, নেপালি, ফিলিপিনো, বার্মিজ এবং থাই সহ অনেক ভাষা সমর্থন করি।" },
        { q: "আমি কি আমার সদস্যতা বাতিল করতে পারি?", a: "হ্যাঁ, আপনি যেকোনো সময় আপনার সদস্যতা বাতিল করতে পারেন।" }
      ]
    },
    contact: {
      title: "যোগাযোগ করুন",
      subtitle: "আমরা আপনাকে সাহায্য করতে এখানে আছি",
      address: "লেভেল 15, মেনারা কেএল\nজালান সুলতান ইসমাইল\n50250 কুয়ালালামপুর",
      phone: "+60 3-1234 5678",
      email: "support@myshield.my",
      hours: "সোম-শুক্র: 9AM - 6PM\nজরুরি: 24/7"
    },
    footer: {
      tagline: "শ্রমিকদের সুরক্ষা, অধিকারের প্রতিরক্ষা।",
      quickLinks: "দ্রুত লিংক",
      legal: "আইনি",
      privacy: "গোপনীয়তা নীতি",
      terms: "সেবার শর্তাবলী",
      copyright: "© 2024 MyShield. সর্বস্বত্ব সংরক্ষিত।"
    },
    cta: {
      title: "সুরক্ষিত হতে প্রস্তুত?",
      subtitle: "হাজার হাজার শ্রমিকের সাথে যোগ দিন যারা তাদের সুরক্ষা এবং সহায়তার জন্য MyShield-এ বিশ্বাস করে।",
      button: "এখনই নিবন্ধন করুন"
    }
  },
  ur: {
    nav: { home: "ہوم", howItWorks: "کیسے کام کرتا ہے", services: "خدمات", pricing: "قیمت", faq: "سوالات", contact: "رابطہ" },
    hero: {
      title: "مزدوروں کی حفاظت، حقوق کا دفاع",
      subtitle: "MyShield ملائیشیا میں غیر ملکی مزدوروں کو جامع مدد فراہم کرتا ہے۔ تنخواہ کے تنازعات سے لے کر قانونی مدد تک، ہم مدد کے لیے یہاں ہیں۔",
      getStarted: "شروع کریں",
      learnMore: "مزید جانیں"
    },
    howItWorks: {
      title: "کیسے کام کرتا ہے",
      subtitle: "مدد حاصل کرنے کے تین آسان قدم",
      steps: [
        { title: "اپنا مسئلہ رپورٹ کریں", description: "اپنی صورتحال کی تفصیلات کے ساتھ ہمارا فارم پُر کریں۔" },
        { title: "مدد حاصل کریں", description: "آپ کے کیس کو سنبھالنے کے لیے ایک وقف عملہ مقرر کیا جائے گا۔" },
        { title: "ٹریک اور حل کریں", description: "اپنے کیس کی پیشرفت کی نگرانی کریں اور ہمارے پلیٹ فارم کے ذریعے براہ راست رابطہ کریں۔" }
      ]
    },
    services: {
      title: "ہماری خدمات",
      subtitle: "تمام مزدور تحفظ کی ضروریات کے لیے جامع مدد",
      items: [
        { title: "تنخواہ کے تنازعات", description: "غیر ادا شدہ اجرت، غیر قانونی کٹوتیوں اور اوور ٹائم دعووں میں مدد۔" },
        { title: "فلاحی فوائد", description: "طبی کوریج، بیمہ کے دعووں اور مزدور معاوضے پر رہنمائی۔" },
        { title: "ہراساں کرنا اور بدسلوکی", description: "کام کی جگہ ہراساں کرنے یا جسمانی بدسلوکی کے لیے خفیہ مدد۔" },
        { title: "معاہدے کے مسائل", description: "معاہدے کی خلاف ورزیوں، غیر منصفانہ شرائط اور روزگار کے تنازعات میں مدد۔" },
        { title: "کام کی جگہ کے حالات", description: "غیر محفوظ کام کے ماحول اور رہائش کے مسائل کی رپورٹنگ۔" },
        { title: "قانونی مدد", description: "مزدور حقوق کے مقدمات کے لیے قانونی مشورے اور نمائندگی تک رسائی۔" }
      ]
    },
    pricing: {
      title: "رکنیت کے منصوبے",
      subtitle: "اپنا تحفظ کا منصوبہ منتخب کریں",
      perMonth: "/مہینہ",
      total: "کل",
      save: "بچائیں",
      fullPrice: "مکمل قیمت",
      off: "چھوٹ",
      bestValue: "بہترین قدر",
      selectPlan: "منصوبہ منتخب کریں",
      plans: [
        { months: 1, pricePerMonth: 20, total: 20, savings: 0, discount: null },
        { months: 3, pricePerMonth: 19, total: 57, savings: 3, discount: "5%" },
        { months: 6, pricePerMonth: 18, total: 108, savings: 12, discount: "10%" },
        { months: 12, pricePerMonth: 17, total: 204, savings: 36, discount: "15%", bestValue: true }
      ]
    },
    faq: {
      title: "اکثر پوچھے گئے سوالات",
      subtitle: "MyShield کے بارے میں وہ سب کچھ جو آپ کو جاننا چاہیے",
      items: [
        { q: "MyShield کون استعمال کر سکتا ہے؟", a: "MyShield ملائیشیا میں تمام غیر ملکی مزدوروں کے لیے ڈیزائن کیا گیا ہے۔" },
        { q: "مجھے کتنی جلدی مدد ملے گی؟", a: "زیادہ تر کیسز 24 گھنٹوں کے اندر تفویض کیے جاتے ہیں۔ ہنگامی کیسز فوری طور پر سنبھالے جاتے ہیں۔" },
        { q: "کیا میری معلومات خفیہ ہے؟", a: "ہاں، آپ کی تمام ذاتی معلومات اور کیس کی تفصیلات سختی سے خفیہ رکھی جاتی ہیں۔" },
        { q: "آپ کن زبانوں کی حمایت کرتے ہیں؟", a: "ہم انگریزی، بہاسا ملائے، تامل، ہندی، بنگالی، نیپالی، فلپائنی، برمی اور تھائی سمیت متعدد زبانوں کی حمایت کرتے ہیں۔" },
        { q: "کیا میں اپنی رکنیت منسوخ کر سکتا ہوں؟", a: "ہاں، آپ کسی بھی وقت اپنی رکنیت منسوخ کر سکتے ہیں۔" }
      ]
    },
    contact: {
      title: "ہم سے رابطہ کریں",
      subtitle: "ہم آپ کی مدد کے لیے یہاں ہیں",
      address: "لیول 15، مینارا کے ایل\nجالان سلطان اسماعیل\n50250 کوالالمپور",
      phone: "+60 3-1234 5678",
      email: "support@myshield.my",
      hours: "سوم-جمعہ: 9AM - 6PM\nایمرجنسی: 24/7"
    },
    footer: {
      tagline: "مزدوروں کی حفاظت، حقوق کا دفاع۔",
      quickLinks: "فوری لنکس",
      legal: "قانونی",
      privacy: "رازداری کی پالیسی",
      terms: "خدمات کی شرائط",
      copyright: "© 2024 MyShield. جملہ حقوق محفوظ ہیں۔"
    },
    cta: {
      title: "محفوظ ہونے کے لیے تیار ہیں؟",
      subtitle: "ہزاروں مزدوروں کے ساتھ شامل ہوں جو اپنے تحفظ اور مدد کی ضروریات کے لیے MyShield پر بھروسہ کرتے ہیں۔",
      button: "ابھی رجسٹر کریں"
    }
  },
  ne: {
    nav: { home: "गृह", howItWorks: "कसरी काम गर्छ", services: "सेवाहरू", pricing: "मूल्य", faq: "प्रश्नहरू", contact: "सम्पर्क" },
    hero: {
      title: "कामदारहरूको सुरक्षा, अधिकारको रक्षा",
      subtitle: "MyShield ले मलेसियामा विदेशी कामदारहरूलाई व्यापक सहयोग प्रदान गर्दछ। तलब विवाददेखि कानुनी सहायतासम्म, हामी मद्दत गर्न यहाँ छौं।",
      getStarted: "सुरु गर्नुहोस्",
      learnMore: "थप जान्नुहोस्"
    },
    howItWorks: {
      title: "कसरी काम गर्छ",
      subtitle: "सहायता प्राप्त गर्न तीन सजिलो चरणहरू",
      steps: [
        { title: "आफ्नो समस्या रिपोर्ट गर्नुहोस्", description: "तपाईंको अवस्थाको विवरणसहित हाम्रो फारम भर्नुहोस्।" },
        { title: "सहयोग प्राप्त गर्नुहोस्", description: "तपाईंको केस सम्हाल्न एक समर्पित कर्मचारी नियुक्त गरिनेछ।" },
        { title: "ट्र्याक र समाधान गर्नुहोस्", description: "तपाईंको केसको प्रगति अनुगमन गर्नुहोस् र हाम्रो प्लेटफर्म मार्फत सिधै सञ्चार गर्नुहोस्।" }
      ]
    },
    services: {
      title: "हाम्रा सेवाहरू",
      subtitle: "सबै कामदार सुरक्षा आवश्यकताहरूका लागि व्यापक सहयोग",
      items: [
        { title: "तलब विवाद", description: "भुक्तानी नगरिएको ज्याला, अवैध कटौती र ओभरटाइम दाबीहरूमा सहायता।" },
        { title: "कल्याण लाभहरू", description: "चिकित्सा कभरेज, बीमा दाबी र कामदार क्षतिपूर्तिमा मार्गदर्शन।" },
        { title: "उत्पीडन र दुर्व्यवहार", description: "कार्यस्थल उत्पीडन वा शारीरिक दुर्व्यवहारका लागि गोप्य सहयोग।" },
        { title: "करार मुद्दाहरू", description: "करार उल्लंघन, अनुचित सर्तहरू र रोजगारी विवादहरूमा मद्दत।" },
        { title: "कार्यस्थल अवस्थाहरू", description: "असुरक्षित कार्य वातावरण र आवास मुद्दाहरू रिपोर्ट गर्ने।" },
        { title: "कानुनी सहयोग", description: "कामदार अधिकार मुद्दाहरूका लागि कानुनी सल्लाह र प्रतिनिधित्वमा पहुँच।" }
      ]
    },
    pricing: {
      title: "सदस्यता योजनाहरू",
      subtitle: "तपाईंको सुरक्षा योजना छान्नुहोस्",
      perMonth: "/महिना",
      total: "जम्मा",
      save: "बचत",
      fullPrice: "पूर्ण मूल्य",
      off: "छुट",
      bestValue: "उत्कृष्ट मूल्य",
      selectPlan: "योजना छान्नुहोस्",
      plans: [
        { months: 1, pricePerMonth: 20, total: 20, savings: 0, discount: null },
        { months: 3, pricePerMonth: 19, total: 57, savings: 3, discount: "5%" },
        { months: 6, pricePerMonth: 18, total: 108, savings: 12, discount: "10%" },
        { months: 12, pricePerMonth: 17, total: 204, savings: 36, discount: "15%", bestValue: true }
      ]
    },
    faq: {
      title: "प्रायः सोधिने प्रश्नहरू",
      subtitle: "MyShield बारे तपाईंलाई थाहा हुनुपर्ने सबै कुरा",
      items: [
        { q: "MyShield कसले प्रयोग गर्न सक्छ?", a: "MyShield मलेसियामा सबै विदेशी कामदारहरूका लागि डिजाइन गरिएको हो।" },
        { q: "मलाई कति छिटो मद्दत मिल्छ?", a: "धेरैजसो केसहरू 24 घण्टाभित्र तोकिन्छन्। आपतकालीन केसहरू तुरुन्तै सम्हालिन्छन्।" },
        { q: "मेरो जानकारी गोप्य छ?", a: "हो, तपाईंको सबै व्यक्तिगत जानकारी र केस विवरणहरू कडाइका साथ गोप्य राखिन्छ।" },
        { q: "तपाईं कुन भाषाहरू समर्थन गर्नुहुन्छ?", a: "हामी अंग्रेजी, बहासा मलाय, तामिल, हिन्दी, बंगाली, नेपाली, फिलिपिनो, बर्मेली र थाई लगायत धेरै भाषाहरू समर्थन गर्छौं।" },
        { q: "के म मेरो सदस्यता रद्द गर्न सक्छु?", a: "हो, तपाईं जुनसुकै समयमा आफ्नो सदस्यता रद्द गर्न सक्नुहुन्छ।" }
      ]
    },
    contact: {
      title: "हामीलाई सम्पर्क गर्नुहोस्",
      subtitle: "हामी तपाईंलाई मद्दत गर्न यहाँ छौं",
      address: "तल्ला 15, मेनारा केएल\nजालान सुल्तान इस्माइल\n50250 कुआलालम्पुर",
      phone: "+60 3-1234 5678",
      email: "support@myshield.my",
      hours: "सोम-शुक्र: 9AM - 6PM\nआपतकालीन: 24/7"
    },
    footer: {
      tagline: "कामदारहरूको सुरक्षा, अधिकारको रक्षा।",
      quickLinks: "द्रुत लिंकहरू",
      legal: "कानुनी",
      privacy: "गोपनीयता नीति",
      terms: "सेवाका सर्तहरू",
      copyright: "© 2024 MyShield. सर्वाधिकार सुरक्षित।"
    },
    cta: {
      title: "सुरक्षित हुन तयार हुनुहुन्छ?",
      subtitle: "हजारौं कामदारहरूसँग सामेल हुनुहोस् जसले आफ्नो सुरक्षा र सहयोग आवश्यकताहरूको लागि MyShield मा विश्वास गर्छन्।",
      button: "अहिले दर्ता गर्नुहोस्"
    }
  },
  fil: {
    nav: { home: "Home", howItWorks: "Paano Gumagana", services: "Mga Serbisyo", pricing: "Presyo", faq: "Mga Tanong", contact: "Kontak" },
    hero: {
      title: "Pagprotekta sa mga Manggagawa, Pagtatanggol ng mga Karapatan",
      subtitle: "Ang MyShield ay nagbibigay ng komprehensibong suporta para sa mga dayuhang manggagawa sa Malaysia. Mula sa mga dispute sa sahod hanggang legal na tulong, naririto kami para tumulong.",
      getStarted: "Magsimula",
      learnMore: "Matuto Pa"
    },
    howItWorks: {
      title: "Paano Gumagana",
      subtitle: "Tatlong simpleng hakbang para makakuha ng tulong",
      steps: [
        { title: "I-report ang Iyong Isyu", description: "Punan ang aming form kasama ang mga detalye ng iyong sitwasyon." },
        { title: "Kumuha ng Suporta", description: "Isang dedikadong staff ang itatalaga para hawakan ang iyong kaso." },
        { title: "Subaybayan at Resolbahin", description: "I-monitor ang progress ng iyong kaso at direktang makipag-usap sa pamamagitan ng aming platform." }
      ]
    },
    services: {
      title: "Ang Aming mga Serbisyo",
      subtitle: "Komprehensibong suporta para sa lahat ng pangangailangan sa proteksyon ng manggagawa",
      items: [
        { title: "Mga Dispute sa Sahod", description: "Tulong sa hindi nabayarang sahod, iligal na kaltas, at overtime claims." },
        { title: "Mga Benepisyo sa Welfare", description: "Gabay sa medical coverage, insurance claims, at worker compensation." },
        { title: "Harassment at Pang-aabuso", description: "Kumpidensyal na suporta para sa workplace harassment o pisikal na pang-aabuso." },
        { title: "Mga Isyu sa Kontrata", description: "Tulong sa paglabag sa kontrata, hindi patas na termino, at employment disputes." },
        { title: "Mga Kondisyon sa Trabaho", description: "Pag-uulat ng hindi ligtas na kapaligiran sa trabaho at mga isyu sa tirahan." },
        { title: "Legal na Suporta", description: "Access sa legal na payo at representasyon para sa mga kaso ng karapatan ng manggagawa." }
      ]
    },
    pricing: {
      title: "Mga Plano sa Membership",
      subtitle: "Piliin ang iyong plano sa proteksyon",
      perMonth: "/buwan",
      total: "Kabuuan",
      save: "Makatipid",
      fullPrice: "Buong presyo",
      off: "diskwento",
      bestValue: "Pinakamahusay na halaga",
      selectPlan: "Piliin ang Plano",
      plans: [
        { months: 1, pricePerMonth: 20, total: 20, savings: 0, discount: null },
        { months: 3, pricePerMonth: 19, total: 57, savings: 3, discount: "5%" },
        { months: 6, pricePerMonth: 18, total: 108, savings: 12, discount: "10%" },
        { months: 12, pricePerMonth: 17, total: 204, savings: 36, discount: "15%", bestValue: true }
      ]
    },
    faq: {
      title: "Mga Madalas na Tanong",
      subtitle: "Lahat ng kailangan mong malaman tungkol sa MyShield",
      items: [
        { q: "Sino ang pwedeng gumamit ng MyShield?", a: "Ang MyShield ay idinisenyo para sa lahat ng dayuhang manggagawa sa Malaysia." },
        { q: "Gaano kabilis ako makakakuha ng tulong?", a: "Karamihan sa mga kaso ay naitatalaga sa loob ng 24 oras. Ang mga emergency case ay agad na hinahawakan." },
        { q: "Kumpidensyal ba ang aking impormasyon?", a: "Oo, lahat ng iyong personal na impormasyon at detalye ng kaso ay mahigpit na kumpidensyal." },
        { q: "Anong mga wika ang sinusuportahan ninyo?", a: "Sumusuporta kami ng maraming wika kabilang ang English, Bahasa Melayu, Tamil, Hindi, Bengali, Nepali, Filipino, Burmese, at Thai." },
        { q: "Pwede ko bang i-cancel ang aking membership?", a: "Oo, pwede mong i-cancel ang iyong membership anumang oras." }
      ]
    },
    contact: {
      title: "Kontakin Kami",
      subtitle: "Naririto kami para tumulong sa iyo",
      address: "Level 15, Menara KL\nJalan Sultan Ismail\n50250 Kuala Lumpur",
      phone: "+60 3-1234 5678",
      email: "support@myshield.my",
      hours: "Lun-Biy: 9AM - 6PM\nEmergency: 24/7"
    },
    footer: {
      tagline: "Pagprotekta sa mga manggagawa, pagtatanggol ng mga karapatan.",
      quickLinks: "Mabilisang Links",
      legal: "Legal",
      privacy: "Privacy Policy",
      terms: "Terms of Service",
      copyright: "© 2024 MyShield. Lahat ng karapatan ay nakalaan."
    },
    cta: {
      title: "Handa nang Maprotektahan?",
      subtitle: "Sumali sa libu-libong manggagawa na nagtitiwala sa MyShield para sa kanilang proteksyon at suporta.",
      button: "Mag-register Ngayon"
    }
  },
  my: {
    nav: { home: "ပင်မ", howItWorks: "အလုပ်လုပ်ပုံ", services: "ဝန်ဆောင်မှုများ", pricing: "စျေးနှုန်း", faq: "မေးခွန်းများ", contact: "ဆက်သွယ်ရန်" },
    hero: {
      title: "အလုပ်သမားများကို ကာကွယ်ခြင်း၊ အခွင့်အရေးများကို ကာကွယ်ခြင်း",
      subtitle: "MyShield သည် မလေးရှားရှိ နိုင်ငံခြားအလုပ်သမားများအတွက် ပြည့်စုံသော ပံ့ပိုးမှုပေးပါသည်။ လစာအငြင်းပွားမှုမှ ဥပဒေရေးရာ အကူအညီအထိ ကျွန်ုပ်တို့ ကူညီရန် ရှိပါသည်။",
      getStarted: "စတင်ပါ",
      learnMore: "ပိုမိုလေ့လာပါ"
    },
    howItWorks: {
      title: "အလုပ်လုပ်ပုံ",
      subtitle: "အကူအညီရယူရန် ရိုးရှင်းသော အဆင့်သုံးဆင့်",
      steps: [
        { title: "သင့်ပြဿနာကို တိုင်ကြားပါ", description: "သင့်အခြေအနေအသေးစိတ်ဖြင့် ကျွန်ုပ်တို့၏ ဖောင်ကို ဖြည့်ပါ။" },
        { title: "ပံ့ပိုးမှုရယူပါ", description: "သင့်ကိစ္စကို ကိုင်တွယ်ရန် သီးသန့်ဝန်ထမ်းတစ်ဦး ခန့်အပ်ပါမည်။" },
        { title: "ခြေရာခံပြီး ဖြေရှင်းပါ", description: "သင့်ကိစ္စတိုးတက်မှုကို စောင့်ကြည့်ပြီး ကျွန်ုပ်တို့၏ ပလက်ဖောင်းမှတဆင့် တိုက်ရိုက်ဆက်သွယ်ပါ။" }
      ]
    },
    services: {
      title: "ကျွန်ုပ်တို့၏ ဝန်ဆောင်မှုများ",
      subtitle: "အလုပ်သမားကာကွယ်မှု လိုအပ်ချက်များအားလုံးအတွက် ပြည့်စုံသော ပံ့ပိုးမှု",
      items: [
        { title: "လစာအငြင်းပွားမှုများ", description: "မပေးရသေးသော လုပ်ခ၊ တရားမဝင်နုတ်ယူမှုများနှင့် အချိန်ပိုတောင်းဆိုမှုများတွင် အကူအညီ။" },
        { title: "ဖူလုံရေးအကျိုးခံစားခွင့်များ", description: "ဆေးဘက်ဆိုင်ရာ အကျုံးဝင်မှု၊ အာမခံတောင်းဆိုမှုများနှင့် အလုပ်သမားလျော်ကြေးအပေါ် လမ်းညွှန်မှု။" },
        { title: "နှောင့်ယှက်မှုနှင့် အလွဲသုံးစားမှု", description: "အလုပ်ခွင်နှောင့်ယှက်မှု သို့မဟုတ် ရုပ်ပိုင်းဆိုင်ရာ အလွဲသုံးစားမှုအတွက် လျှို့ဝှက်ပံ့ပိုးမှု။" },
        { title: "စာချုပ်ပြဿနာများ", description: "စာချုပ်ချိုးဖောက်မှု၊ မမျှတသော စည်းကမ်းချက်များနှင့် အလုပ်အကိုင်အငြင်းပွားမှုများတွင် အကူအညီ။" },
        { title: "အလုပ်ခွင်အခြေအနေများ", description: "မလုံခြုံသော အလုပ်ပတ်ဝန်းကျင်နှင့် နေထိုင်ရေးပြဿနာများကို တိုင်ကြားခြင်း။" },
        { title: "ဥပဒေရေးရာ ပံ့ပိုးမှု", description: "အလုပ်သမားအခွင့်အရေးကိစ္စများအတွက် ဥပဒေအကြံဉာဏ်နှင့် ကိုယ်စားပြုမှုကို ရယူခွင့်။" }
      ]
    },
    pricing: {
      title: "အဖွဲ့ဝင်အစီအစဉ်များ",
      subtitle: "သင့်ကာကွယ်မှုအစီအစဉ်ကို ရွေးချယ်ပါ",
      perMonth: "/လ",
      total: "စုစုပေါင်း",
      save: "သက်သာ",
      fullPrice: "အပြည့်အဝစျေးနှုန်း",
      off: "လျှော့စျေး",
      bestValue: "အကောင်းဆုံးတန်ဖိုး",
      selectPlan: "အစီအစဉ်ရွေးချယ်ပါ",
      plans: [
        { months: 1, pricePerMonth: 20, total: 20, savings: 0, discount: null },
        { months: 3, pricePerMonth: 19, total: 57, savings: 3, discount: "5%" },
        { months: 6, pricePerMonth: 18, total: 108, savings: 12, discount: "10%" },
        { months: 12, pricePerMonth: 17, total: 204, savings: 36, discount: "15%", bestValue: true }
      ]
    },
    faq: {
      title: "မကြာခဏမေးလေ့ရှိသော မေးခွန်းများ",
      subtitle: "MyShield အကြောင်း သင်သိထားသင့်သမျှ",
      items: [
        { q: "MyShield ကို ဘယ်သူတွေ သုံးလို့ရလဲ?", a: "MyShield ကို မလေးရှားရှိ နိုင်ငံခြားအလုပ်သမားအားလုံးအတွက် ဒီဇိုင်းထုတ်ထားပါသည်။" },
        { q: "ကျွန်တော်/မ ဘယ်လောက်မြန်မြန် အကူအညီရမလဲ?", a: "ကိစ္စအများစုကို ၂၄ နာရီအတွင်း ခန့်အပ်ပါသည်။ အရေးပေါ်ကိစ္စများကို ချက်ချင်းကိုင်တွယ်ပါသည်။" },
        { q: "ကျွန်တော်/မ ရဲ့ အချက်အလက်တွေ လျှို့ဝှက်ထားလား?", a: "ဟုတ်ကဲ့၊ သင်၏ကိုယ်ရေးအချက်အလက်နှင့် ကိစ္စအသေးစိတ်များအားလုံးကို တင်းကြပ်စွာ လျှို့ဝှက်ထားပါသည်။" },
        { q: "ဘာဘာသာစကားတွေကို ပံ့ပိုးပေးလဲ?", a: "အင်္ဂလိပ်၊ ဘာဟာဆာမလေး၊ တမီးလ်၊ ဟိန္ဒီ၊ ဘင်္ဂါလီ၊ နီပေါ၊ ဖိလစ်ပိုင်၊ ဗမာနှင့် ထိုင်းအပါအဝင် ဘာသာစကားများစွာကို ပံ့ပိုးပေးပါသည်။" },
        { q: "ကျွန်တော်/မ ရဲ့ အဖွဲ့ဝင်မှုကို ဖျက်သိမ်းလို့ရလား?", a: "ဟုတ်ကဲ့၊ သင်သည် မည်သည့်အချိန်တွင်မဆို သင်၏အဖွဲ့ဝင်မှုကို ဖျက်သိမ်းနိုင်ပါသည်။" }
      ]
    },
    contact: {
      title: "ဆက်သွယ်ပါ",
      subtitle: "ကျွန်ုပ်တို့ သင့်ကို ကူညီရန် ရှိပါသည်",
      address: "အဆင့် ၁၅၊ မီနာရာ ကေအယ်လ်\nဂျာလန် ဆူလ်တန် အစ္စမိုင်လ်\n၅၀၂၅၀ ကွာလာလမ်ပူ",
      phone: "+60 3-1234 5678",
      email: "support@myshield.my",
      hours: "တနင်္လာ-သောကြာ: 9AM - 6PM\nအရေးပေါ်: 24/7"
    },
    footer: {
      tagline: "အလုပ်သမားများကို ကာကွယ်ခြင်း၊ အခွင့်အရေးများကို ကာကွယ်ခြင်း။",
      quickLinks: "အမြန်လင့်များ",
      legal: "ဥပဒေ",
      privacy: "ကိုယ်ရေးလုံခြုံမှု မူဝါဒ",
      terms: "ဝန်ဆောင်မှုစည်းမျဉ်းများ",
      copyright: "© 2024 MyShield. မူပိုင်ခွင့်များအားလုံး ရယူထားသည်။"
    },
    cta: {
      title: "ကာကွယ်မှုရယူဖို့ အသင့်ဖြစ်ပြီလား?",
      subtitle: "သူတို့၏ ကာကွယ်မှုနှင့် ပံ့ပိုးမှုလိုအပ်ချက်များအတွက် MyShield ကို ယုံကြည်သော အလုပ်သမားထောင်ပေါင်းများစွာနှင့် ပူးပေါင်းပါ။",
      button: "ယခုစာရင်းသွင်းပါ"
    }
  },
  th: {
    nav: { home: "หน้าแรก", howItWorks: "วิธีการทำงาน", services: "บริการ", pricing: "ราคา", faq: "คำถาม", contact: "ติดต่อ" },
    hero: {
      title: "ปกป้องแรงงาน พิทักษ์สิทธิ์",
      subtitle: "MyShield ให้การสนับสนุนอย่างครอบคลุมสำหรับแรงงานต่างชาติในมาเลเซีย ตั้งแต่ข้อพิพาทด้านค่าจ้างไปจนถึงความช่วยเหลือทางกฎหมาย เราอยู่ที่นี่เพื่อช่วยเหลือ",
      getStarted: "เริ่มต้น",
      learnMore: "เรียนรู้เพิ่มเติม"
    },
    howItWorks: {
      title: "วิธีการทำงาน",
      subtitle: "สามขั้นตอนง่ายๆ ในการรับความช่วยเหลือ",
      steps: [
        { title: "รายงานปัญหาของคุณ", description: "กรอกแบบฟอร์มพร้อมรายละเอียดสถานการณ์ของคุณ" },
        { title: "รับการสนับสนุน", description: "เจ้าหน้าที่เฉพาะทางจะได้รับมอบหมายให้ดูแลเคสของคุณ" },
        { title: "ติดตามและแก้ไข", description: "ติดตามความคืบหน้าของเคสและสื่อสารโดยตรงผ่านแพลตฟอร์มของเรา" }
      ]
    },
    services: {
      title: "บริการของเรา",
      subtitle: "การสนับสนุนอย่างครอบคลุมสำหรับความต้องการด้านการคุ้มครองแรงงานทั้งหมด",
      items: [
        { title: "ข้อพิพาทด้านค่าจ้าง", description: "ความช่วยเหลือเกี่ยวกับค่าจ้างที่ค้างชำระ การหักเงินที่ผิดกฎหมาย และการเรียกร้องค่าล่วงเวลา" },
        { title: "สวัสดิการ", description: "คำแนะนำเกี่ยวกับความคุ้มครองทางการแพทย์ การเรียกร้องประกันภัย และค่าชดเชยแรงงาน" },
        { title: "การคุกคามและการละเมิด", description: "การสนับสนุนอย่างเป็นความลับสำหรับการคุกคามในที่ทำงานหรือการละเมิดทางกาย" },
        { title: "ปัญหาสัญญา", description: "ความช่วยเหลือในการละเมิดสัญญา เงื่อนไขที่ไม่เป็นธรรม และข้อพิพาทด้านการจ้างงาน" },
        { title: "สภาพการทำงาน", description: "รายงานสภาพแวดล้อมการทำงานที่ไม่ปลอดภัยและปัญหาที่พัก" },
        { title: "การสนับสนุนทางกฎหมาย", description: "การเข้าถึงคำปรึกษาทางกฎหมายและการเป็นตัวแทนสำหรับคดีสิทธิแรงงาน" }
      ]
    },
    pricing: {
      title: "แผนสมาชิก",
      subtitle: "เลือกแผนการคุ้มครองของคุณ",
      perMonth: "/เดือน",
      total: "รวม",
      save: "ประหยัด",
      fullPrice: "ราคาเต็ม",
      off: "ส่วนลด",
      bestValue: "คุ้มค่าที่สุด",
      selectPlan: "เลือกแผน",
      plans: [
        { months: 1, pricePerMonth: 20, total: 20, savings: 0, discount: null },
        { months: 3, pricePerMonth: 19, total: 57, savings: 3, discount: "5%" },
        { months: 6, pricePerMonth: 18, total: 108, savings: 12, discount: "10%" },
        { months: 12, pricePerMonth: 17, total: 204, savings: 36, discount: "15%", bestValue: true }
      ]
    },
    faq: {
      title: "คำถามที่พบบ่อย",
      subtitle: "ทุกสิ่งที่คุณต้องรู้เกี่ยวกับ MyShield",
      items: [
        { q: "ใครสามารถใช้ MyShield ได้?", a: "MyShield ออกแบบมาสำหรับแรงงานต่างชาติทุกคนในมาเลเซีย" },
        { q: "ฉันจะได้รับความช่วยเหลือเร็วแค่ไหน?", a: "เคสส่วนใหญ่ได้รับมอบหมายภายใน 24 ชั่วโมง เคสฉุกเฉินจะได้รับการจัดการทันที" },
        { q: "ข้อมูลของฉันเป็นความลับหรือไม่?", a: "ใช่ ข้อมูลส่วนบุคคลและรายละเอียดเคสทั้งหมดของคุณจะถูกเก็บเป็นความลับอย่างเข้มงวด" },
        { q: "คุณสนับสนุนภาษาอะไรบ้าง?", a: "เราสนับสนุนหลายภาษารวมถึงอังกฤษ บาฮาซามาเลย์ ทมิฬ ฮินดี เบงกาลี เนปาล ฟิลิปปินส์ พม่า และไทย" },
        { q: "ฉันสามารถยกเลิกการเป็นสมาชิกได้หรือไม่?", a: "ได้ คุณสามารถยกเลิกการเป็นสมาชิกได้ทุกเมื่อ" }
      ]
    },
    contact: {
      title: "ติดต่อเรา",
      subtitle: "เราพร้อมช่วยเหลือคุณ",
      address: "ชั้น 15 เมนารา เคแอล\nจาลัน สุลต่าน อิสมาอิล\n50250 กัวลาลัมเปอร์",
      phone: "+60 3-1234 5678",
      email: "support@myshield.my",
      hours: "จันทร์-ศุกร์: 9.00 - 18.00 น.\nฉุกเฉิน: 24/7"
    },
    footer: {
      tagline: "ปกป้องแรงงาน พิทักษ์สิทธิ์",
      quickLinks: "ลิงก์ด่วน",
      legal: "กฎหมาย",
      privacy: "นโยบายความเป็นส่วนตัว",
      terms: "ข้อกำหนดการให้บริการ",
      copyright: "© 2024 MyShield สงวนลิขสิทธิ์"
    },
    cta: {
      title: "พร้อมที่จะได้รับการคุ้มครอง?",
      subtitle: "เข้าร่วมกับแรงงานหลายพันคนที่ไว้วางใจ MyShield สำหรับความต้องการด้านการคุ้มครองและการสนับสนุนของพวกเขา",
      button: "ลงทะเบียนเลย"
    }
  }
};

// Helper to get nested translation
const getTranslation = (lang, key) => {
  const keys = key.split('.');
  let result = translations[lang] || translations.en;
  for (const k of keys) {
    result = result?.[k];
    if (result === undefined) {
      // Fallback to English
      result = translations.en;
      for (const k2 of keys) {
        result = result?.[k2];
      }
      break;
    }
  }
  return result;
};

export default function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState(() => {
    // Load saved language from localStorage or default to 'en'
    return localStorage.getItem('myshield_lang') || 'en';
  });
  const [activeSection, setActiveSection] = useState('home'); // For mobile section navigation
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();

  // Detect mobile screen
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024); // lg breakpoint
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Get current translations
  const t = (key) => getTranslation(currentLang, key);

  // Save language preference when changed
  const handleLanguageChange = (langCode) => {
    setCurrentLang(langCode);
    localStorage.setItem('myshield_lang', langCode);
    setLangDropdownOpen(false);
    setMobileMenuOpen(false);
  };

  const navLinks = [
    { name: t('nav.home'), href: "#home" },
    { name: t('nav.howItWorks'), href: "#how-it-works" },
    { name: t('nav.services'), href: "#services" },
    { name: t('nav.pricing'), href: "#pricing" },
    { name: t('nav.faq'), href: "#faq" },
    { name: t('nav.contact'), href: "#contact" },
  ];

  const languages = [
    { code: "en", name: "English", flag: "🇬🇧" },
    { code: "ms", name: "Bahasa Melayu", flag: "🇲🇾" },
    { code: "ta", name: "தமிழ்", flag: "🇮🇳" },
    { code: "hi", name: "हिन्दी (Hindi)", flag: "🇮🇳" },
    { code: "bn", name: "বাংলা (Bengali)", flag: "🇧🇩" },
    { code: "ur", name: "اردو (Urdu)", flag: "🇵🇰" },
    { code: "ne", name: "नेपाली (Nepali)", flag: "🇳🇵" },
    { code: "fil", name: "Filipino", flag: "🇵🇭" },
    { code: "my", name: "မြန်မာ (Burmese)", flag: "🇲🇲" },
    { code: "th", name: "ไทย (Thai)", flag: "🇹🇭" },
  ];

  const serviceIcons = [
    <FileText className="w-8 h-8" />,
    <Heart className="w-8 h-8" />,
    <AlertTriangle className="w-8 h-8" />,
    <Briefcase className="w-8 h-8" />,
    <Users className="w-8 h-8" />,
    <Scale className="w-8 h-8" />,
  ];

  const steps = t('howItWorks.steps') || [];

  const scrollToSection = (href) => {
    setMobileMenuOpen(false);
    const sectionId = href.replace('#', '');
    
    // Tab-like behavior for both mobile and desktop
    setActiveSection(sectionId);
    // Scroll to top of content area
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Helper to check if section should be visible (tab-based for all views)
  const isSectionVisible = (sectionId) => {
    return activeSection === sectionId;
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm z-50 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_protect-workers/artifacts/awekt278_image.png" 
                alt="MyShield Logo" 
                className="h-14 w-auto"
              />
              <span className="text-xl font-bold text-slate-900">MyShield</span>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => {
                const sectionId = link.href.replace('#', '');
                const isActive = activeSection === sectionId;
                return (
                  <button
                    key={link.name}
                    onClick={() => scrollToSection(link.href)}
                    className={`px-4 py-2 rounded-lg font-medium text-sm transition ${
                      isActive
                        ? 'bg-slate-900 text-white'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                    }`}
                    data-testid={`desktop-tab-${sectionId}`}
                  >
                    {link.name}
                  </button>
                );
              })}
            </div>

            {/* Right side - Desktop */}
            <div className="hidden lg:flex items-center gap-4">
              {/* Language Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setLangDropdownOpen(!langDropdownOpen)}
                  className="flex items-center gap-2 text-slate-600 hover:text-slate-900 text-sm"
                  data-testid="lang-dropdown-toggle"
                >
                  <Globe className="w-4 h-4" />
                  <span>{languages.find(l => l.code === currentLang)?.name || 'English'}</span>
                  <ChevronDown className="w-4 h-4" />
                </button>
                {langDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-2 z-50">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        className={`w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2 ${
                          currentLang === lang.code ? "bg-slate-100 font-semibold" : ""
                        }`}
                        onClick={() => handleLanguageChange(lang.code)}
                        data-testid={`lang-${lang.code}`}
                      >
                        <span>{lang.flag}</span>
                        <span>{lang.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <Link to="/register">
                <Button variant="outline" size="sm" className="border-amber-500 text-amber-600 hover:bg-amber-50">Register</Button>
              </Link>
              <Link to="/login">
                <Button size="sm" className="bg-slate-900 hover:bg-slate-800">Login</Button>
              </Link>
            </div>

            {/* Mobile menu button */}
            <button
              className="lg:hidden p-2 rounded-md text-slate-600 hover:bg-slate-100"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="landing-mobile-menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        {mobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 top-16 z-40">
            <div className="absolute inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)} />
            <div className="absolute left-0 top-0 right-0 bg-white shadow-xl animate-slide-down max-h-[calc(100vh-4rem)] overflow-y-auto">
              <div className="p-6 space-y-6">
                {/* Mobile Nav Links */}
                <div className="space-y-1">
                  {navLinks.map((link) => (
                    <button
                      key={link.name}
                      onClick={() => scrollToSection(link.href)}
                      className="block w-full text-left text-slate-700 hover:text-slate-900 hover:bg-slate-50 font-medium py-3 px-2 rounded-lg transition"
                    >
                      {link.name}
                    </button>
                  ))}
                </div>

                {/* Mobile Actions - Right after nav links */}
                <div className="pt-4 border-t border-slate-200 space-y-3">
                  <Link to="/register" className="block" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="outline" className="w-full border-amber-500 text-amber-600 hover:bg-amber-50">Register Now</Button>
                  </Link>
                  <Link to="/login" className="block" onClick={() => setMobileMenuOpen(false)}>
                    <Button className="w-full bg-slate-900 hover:bg-slate-800">Login</Button>
                  </Link>
                </div>

                {/* Mobile Language Selector */}
                <div className="pt-4 border-t border-slate-200">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-3">Language</p>
                  <div className="grid grid-cols-2 gap-2">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => handleLanguageChange(lang.code)}
                        className={`text-left text-sm py-2 px-2 rounded-lg flex items-center gap-2 transition ${
                          currentLang === lang.code 
                            ? "bg-slate-100 text-slate-900 font-semibold" 
                            : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                        }`}
                        data-testid={`mobile-lang-${lang.code}`}
                      >
                        <span>{lang.flag}</span>
                        <span className="truncate text-xs">{lang.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Animation styles */}
        <style>{`
          @keyframes slideDown {
            from { transform: translateY(-100%); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }
          .animate-slide-down {
            animation: slideDown 0.3s ease-out;
          }
        `}</style>
      </nav>

      {/* Mobile Section Tab Bar - Only visible on mobile when not on home */}
      {isMobile && (
        <div className="fixed top-16 left-0 right-0 bg-white border-b border-slate-200 z-40 overflow-x-auto">
          <div className="flex px-2 py-2 gap-1 min-w-max">
            {navLinks.map((link) => {
              const sectionId = link.href.replace('#', '');
              const isActive = activeSection === sectionId;
              return (
                <button
                  key={link.name}
                  onClick={() => scrollToSection(link.href)}
                  className={`px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                    isActive
                      ? 'bg-slate-900 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid={`mobile-tab-${sectionId}`}
                >
                  {link.name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Hero Section */}
      {isSectionVisible('home') && (
      <section id="home" className={`relative min-h-screen flex items-center justify-center ${isMobile ? 'pt-28' : 'pt-16'}`}>
        {/* Background Image with Overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1504307651254-35680f356dfd?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80')`,
          }}
        >
          <div className="absolute inset-0 bg-slate-900/75" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-amber-500/20 border border-amber-500/40 rounded-full px-4 py-2 mb-8">
            <Shield className="w-4 h-4 text-amber-400" />
            <span className="text-amber-300 text-sm font-medium">Official Worker Protection Portal</span>
          </div>

          {/* Main Heading */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" data-testid="hero-title">
            {t('hero.title')}
          </h1>

          {/* Subtitle */}
          <p className="text-lg sm:text-xl text-slate-300 max-w-3xl mx-auto mb-10 leading-relaxed">
            {t('hero.subtitle')}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/register">
              <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-white px-8 py-6 text-lg">
                {t('hero.getStarted')}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <button 
              onClick={() => scrollToSection("#how-it-works")}
              className="text-white border border-white/30 hover:bg-white/10 px-8 py-3 rounded-lg text-lg font-medium transition"
            >
              {t('hero.learnMore')}
            </button>
          </div>
        </div>

        {/* Scroll Indicator - only on desktop */}
        {!isMobile && (
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <ChevronDown className="w-8 h-8 text-white/50" />
        </div>
        )}
      </section>
      )}

      {/* How We Support You Section */}
      {isSectionVisible('how-it-works') && (
      <section id="how-it-works" className={`py-24 bg-white min-h-screen ${isMobile ? 'pt-28' : 'pt-20'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              {t('howItWorks.title')}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t('howItWorks.subtitle')}
            </p>
          </div>

          {/* Steps */}
          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            {steps.map((item, index) => (
              <div key={index} className="relative">
                {/* Connector Line (hidden on mobile) */}
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-12 left-1/2 w-full h-0.5 bg-slate-200" />
                )}
                
                <div className="relative flex flex-col items-center text-center">
                  {/* Step Number */}
                  <div className="w-24 h-24 rounded-full bg-slate-100 flex items-center justify-center mb-6 relative z-10">
                    <div className="w-16 h-16 rounded-full bg-slate-800 flex items-center justify-center">
                      {index === 0 && <FileText className="w-7 h-7 text-white" />}
                      {index === 1 && <Users className="w-7 h-7 text-white" />}
                      {index === 2 && <CheckCircle className="w-7 h-7 text-white" />}
                    </div>
                  </div>

                  {/* Content Card */}
                  <Card className="w-full border-none shadow-lg">
                    <CardContent className="pt-6 pb-8 px-6">
                      <p className="text-amber-500 text-sm font-semibold mb-2">Step {item.step}</p>
                      <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
                      <p className="text-slate-600">{item.description}</p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Services Section */}
      {isSectionVisible('services') && (
      <section id="services" className={`py-24 bg-slate-50 min-h-screen ${isMobile ? 'pt-28' : 'pt-20'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              {t('services.title')}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t('services.subtitle')}
            </p>
          </div>

          {/* Services Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {(t('services.items') || []).map((service, index) => (
              <Card key={index} className="border-none shadow-md hover:shadow-xl transition-shadow bg-white">
                <CardContent className="pt-8 pb-8 px-6">
                  {/* Icon */}
                  <div className="w-14 h-14 rounded-xl bg-slate-100 flex items-center justify-center mb-6 text-slate-700">
                    {serviceIcons[index]}
                  </div>
                  
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{service.title}</h3>
                  <p className="text-slate-600 mb-6">{service.description}</p>
                  
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => navigate('/register?benefit=' + encodeURIComponent(service.title))}
                    data-testid={`service-learn-more-${index}`}
                  >
                    {t('hero.learnMore')}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Pricing Section */}
      {isSectionVisible('pricing') && (
      <section id="pricing" className={`py-24 bg-white min-h-screen ${isMobile ? 'pt-28' : 'pt-20'}`}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              {t('pricing.title')}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t('pricing.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {(t('pricing.plans') || []).map((plan, index) => (
              <Card 
                key={index} 
                className={`relative cursor-pointer transition-all hover:scale-105 ${
                  plan.bestValue 
                    ? 'border-2 border-blue-500 shadow-lg shadow-blue-500/20' 
                    : 'border border-slate-200 hover:border-slate-300'
                }`}
                onClick={() => navigate(`/payment?plan=${plan.months}`)}
                data-testid={`pricing-plan-${plan.months}m`}
              >
                {plan.bestValue && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-blue-500 text-white text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap">
                      {t('pricing.bestValue')}
                    </span>
                  </div>
                )}
                <CardContent className="pt-8 pb-6 px-4 text-center">
                  {/* Duration */}
                  <div className="text-3xl lg:text-4xl font-bold text-slate-900 mb-1">
                    {plan.months}
                  </div>
                  <div className="text-slate-500 text-sm mb-4">
                    {plan.months === 1 ? 'month' : 'months'}
                  </div>
                  
                  {/* Price per month */}
                  <div className="mb-3">
                    <span className="text-2xl lg:text-3xl font-bold text-slate-900">RM {plan.pricePerMonth}</span>
                    <span className="text-slate-500 text-sm">{t('pricing.perMonth')}</span>
                  </div>
                  
                  {/* Total */}
                  <div className="text-slate-600 text-sm mb-3">
                    {t('pricing.total')}: <span className="font-semibold">RM {plan.total}</span>
                  </div>
                  
                  {/* Savings or Full Price */}
                  {plan.savings > 0 ? (
                    <div className="space-y-1">
                      <div className="text-emerald-600 font-medium text-sm">
                        {t('pricing.save')} RM {plan.savings}
                      </div>
                      <div className="inline-block bg-emerald-100 text-emerald-700 text-xs font-semibold px-2 py-1 rounded">
                        {plan.discount} {t('pricing.off')}
                      </div>
                    </div>
                  ) : (
                    <div className="text-slate-400 text-sm">
                      {t('pricing.fullPrice')}
                    </div>
                  )}
                  
                  {/* Select Button */}
                  <Button 
                    className={`w-full mt-4 ${
                      plan.bestValue 
                        ? 'bg-blue-500 hover:bg-blue-600' 
                        : 'bg-slate-900 hover:bg-slate-800'
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/payment?plan=${plan.months}`);
                    }}
                  >
                    {t('pricing.selectPlan')}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* FAQ Section */}
      {isSectionVisible('faq') && (
      <section id="faq" className={`py-24 bg-slate-50 min-h-screen ${isMobile ? 'pt-28' : 'pt-20'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              {t('faq.title')}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t('faq.subtitle')}
            </p>
          </div>

          <div className="space-y-6">
            {(t('faq.items') || []).map((faq, index) => (
              <Card key={index} className="border-none shadow-md">
                <CardContent className="pt-6 pb-6 px-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{faq.q}</h3>
                  <p className="text-slate-600">{faq.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Contact Section */}
      {isSectionVisible('contact') && (
      <section id="contact" className={`py-24 bg-slate-900 min-h-screen ${isMobile ? 'pt-28' : 'pt-20'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                {t('contact.title')}
              </h2>
              <p className="text-slate-400 text-lg mb-8">
                {t('contact.subtitle')}
              </p>

              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-slate-800 flex items-center justify-center">
                    <Phone className="w-5 h-5 text-amber-500" />
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm">Phone</p>
                    <p className="text-white font-medium">{t('contact.phone')}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-slate-800 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-amber-500" />
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm">Email</p>
                    <p className="text-white font-medium">{t('contact.email')}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-slate-800 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-amber-500" />
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm">Address</p>
                    <p className="text-white font-medium whitespace-pre-line">{t('contact.address')}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <Card className="border-none bg-white">
              <CardContent className="pt-8 pb-8 px-8">
                <h3 className="text-xl font-bold text-slate-900 mb-6">Send us a message</h3>
                <form className="space-y-4">
                  <div>
                    <input
                      type="text"
                      placeholder="Your Name"
                      className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                  <div>
                    <input
                      type="email"
                      placeholder="Your Email"
                      className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                  <div>
                    <textarea
                      placeholder="Your Message"
                      rows={4}
                      className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-amber-500"
                    />
                  </div>
                  <Button className="w-full bg-amber-500 hover:bg-amber-600">
                    Send Message
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
      )}

      {/* Footer - Always visible */}
      <footer className="bg-slate-950 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <Shield className="w-6 h-6 text-slate-400" />
              <span className="text-slate-400 font-medium">MyShield</span>
            </div>
            <p className="text-slate-500 text-sm text-center">
              {t('footer.copyright')}
            </p>
            <div className="flex items-center gap-6">
              <a href="#" className="text-slate-400 hover:text-white text-sm">{t('footer.privacy')}</a>
              <a href="#" className="text-slate-400 hover:text-white text-sm">{t('footer.terms')}</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Mobile Menu Animation Styles */}
      <style>{`
        @keyframes slideIn {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in {
          animation: slideIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
